import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, LogOut, MessageSquare, Settings, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/button';
import { DISPLAY_PREFERENCES_HASH } from './DisplayPreferencesCard';
import { useTextSize, type TextSize } from '@/hooks/useTextSize';
import { useLanguagePreference } from '@/i18n/useLanguagePreference';
import { cn } from '@/lib/utils';

/**
 * Quick tool controls next to "Logged in as": text size, language, a link to
 * the full Display preferences card on Profile, feedback, and log out.
 *
 * Text size and language stay here so they are one click away during a patient
 * review — the same hooks drive Profile (`TextSizeSwitcher`, LocaleSelect), so
 * either surface stays in sync. Theme, font, meal photos, summary layout, and
 * the rest live only on Profile; this menu deep-links there rather than
 * duplicating them.
 *
 * The panel opens upward (the trigger is pinned to the bottom of the rail),
 * and to the right when the rail is collapsed to icons — at that width there
 * is no room for it to sit above the 56px trigger.
 */
type TextSizeOption = {
  value: TextSize;
  label: string;
  /** Tailwind class used to render the "A" preview at its real size, so the
   *  visual is also the affordance (the pattern BBC / gov.uk / NYT use). */
  preview: string;
};

const TEXT_SIZES: TextSizeOption[] = [
  { value: 'small', label: 'Small', preview: 'text-xs' },
  { value: 'normal', label: 'Default', preview: 'text-sm' },
  { value: 'large', label: 'Large', preview: 'text-base' },
  { value: 'x-large', label: 'Extra large', preview: 'text-lg' },
];

type Props = {
  /** Rail is collapsed to icons — render an icon-only trigger and open the
   *  panel to the side instead of above. */
  collapsed: boolean;
  onOpenFeedback: () => void;
  onLogout: () => void;
};

export const PreferencesMenu = ({ collapsed, onOpenFeedback, onLogout }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [size, setSize] = useTextSize();
  const language = useLanguagePreference();
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapper} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Preferences')}
        title={t('Preferences')}
        className={cn('w-full justify-start gap-2', collapsed && 'justify-center px-2')}
      >
        <Settings className="h-4 w-4 shrink-0" />
        {!collapsed && <span>{t('Preferences')}</span>}
      </Button>

      {open && (
        <div
          role="menu"
          aria-label={t('Preferences')}
          className={cn(
            'absolute bottom-full z-50 mb-1 w-56 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
            // Collapsed rail is 56px wide — anchoring left would put the
            // panel mostly off-screen, so it flies out to the right instead.
            collapsed ? 'left-full ml-1' : 'left-0',
          )}
        >
          <MenuSection label={t('Text size')}>
            {TEXT_SIZES.map((opt) => (
              <MenuRadio
                key={opt.value}
                active={opt.value === size}
                onClick={() => setSize(opt.value)}
              >
                <span className="flex items-baseline gap-3">
                  <span className={cn('w-6 text-center font-semibold', opt.preview)}>A</span>
                  <span>{t(opt.label)}</span>
                </span>
              </MenuRadio>
            ))}
          </MenuSection>

          <MenuSection label={t('Language')}>
            {language.languages.map((lang) => (
              <MenuRadio
                key={lang.code}
                active={lang.code === language.current}
                onClick={() => language.choose(lang.code)}
              >
                <span>{lang.label}</span>
              </MenuRadio>
            ))}
          </MenuSection>

          <div className="border-t py-1">
            <Link
              to={`/account#${DISPLAY_PREFERENCES_HASH}`}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t('Display preferences')}
            </Link>
            <MenuAction
              icon={<MessageSquare className="h-4 w-4" />}
              onClick={() => {
                setOpen(false);
                onOpenFeedback();
              }}
            >
              {t('Feedback')}
            </MenuAction>
            <MenuAction
              icon={<LogOut className="h-4 w-4" />}
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              {t('Log out')}
            </MenuAction>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="border-b py-1 last:border-b-0">
    <p className="px-3 pb-1 pt-1.5 text-[0.625rem] uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    {children}
  </div>
);

const MenuRadio = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    role="menuitemradio"
    aria-checked={active}
    onClick={onClick}
    className={cn(
      'flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-muted',
      active && 'font-medium',
    )}
  >
    {children}
    {active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
  </button>
);

const MenuAction = ({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
  >
    {icon}
    {children}
  </button>
);
